import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Search, Building2, FolderLock, X, Check } from "lucide-react";
import { toast } from "sonner";
import client from "@/api/client";

const ROOM_ROLES = ["viewer", "editor", "owner"];

/**
 * AdminUserAccessDialog — one place for admins to manage a single user's
 * privileges: link/unlink their Company, and grant/revoke access to any
 * data room (with a per-room role). Kept as a single dialog so admins
 * don't need to hop between three different manager pages.
 */
export function AdminUserAccessDialog({ open, onOpenChange, user, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [detail, setDetail] = useState(null); // { user, company, data_rooms }
  const [companies, setCompanies] = useState([]);
  const [rooms, setRooms] = useState([]);
  const [companyQuery, setCompanyQuery] = useState("");
  const [roomQuery, setRoomQuery] = useState("");
  const [savingCompany, setSavingCompany] = useState(false);
  const [savingRoom, setSavingRoom] = useState(null); // room_id being toggled

  const userId = user?.user_id;

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const [detailRes, cRes, rRes] = await Promise.all([
        client.get(`/admin/users/${userId}`),
        client.get(`/admin/companies?per_page=200`),
        client.get(`/admin/data-rooms?per_page=200`),
      ]);
      setDetail(detailRes.data);
      setCompanies(cRes.data?.items || []);
      setRooms(rRes.data?.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load user access");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => {
    if (open) {
      setCompanyQuery("");
      setRoomQuery("");
      setDetail(null);
      load();
    }
  }, [open, load]);

  const linkedRoomIds = useMemo(() => {
    const s = new Set();
    (detail?.data_rooms || []).forEach((r) => s.add(r.room_id));
    return s;
  }, [detail]);

  const linkedRoomRoles = useMemo(() => {
    const map = {};
    (detail?.data_rooms || []).forEach((r) => (map[r.room_id] = r.role || "viewer"));
    return map;
  }, [detail]);

  const filteredCompanies = useMemo(() => {
    const q = companyQuery.trim().toLowerCase();
    if (!q) return companies.slice(0, 20);
    return companies
      .filter((c) => (c.name || "").toLowerCase().includes(q))
      .slice(0, 20);
  }, [companies, companyQuery]);

  const filteredRooms = useMemo(() => {
    const q = roomQuery.trim().toLowerCase();
    if (!q) return rooms;
    return rooms.filter((r) =>
      ((r.company_name || "") + " " + (r.description || "")).toLowerCase().includes(q),
    );
  }, [rooms, roomQuery]);

  const linkCompany = async (company_id) => {
    setSavingCompany(true);
    try {
      const res = await client.patch(`/admin/users/${userId}`, { company_id: company_id || null });
      setDetail((d) => (d ? { ...d, user: res.data } : d));
      // Reload detail to refresh the linked-company block.
      const fresh = await client.get(`/admin/users/${userId}`);
      setDetail(fresh.data);
      onSaved && onSaved(res.data);
      toast.success(company_id ? "Company linked" : "Company unlinked");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to update company");
    } finally {
      setSavingCompany(false);
    }
  };

  const grantRoom = async (room_id, role = "viewer") => {
    setSavingRoom(room_id);
    try {
      await client.post(`/admin/users/${userId}/data-rooms`, { room_id, role });
      const fresh = await client.get(`/admin/users/${userId}`);
      setDetail(fresh.data);
      onSaved && onSaved(fresh.data?.user);
      toast.success("Access granted");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to grant access");
    } finally {
      setSavingRoom(null);
    }
  };

  const revokeRoom = async (room_id) => {
    setSavingRoom(room_id);
    try {
      await client.delete(`/admin/users/${userId}/data-rooms/${room_id}`);
      const fresh = await client.get(`/admin/users/${userId}`);
      setDetail(fresh.data);
      onSaved && onSaved(fresh.data?.user);
      toast.success("Access revoked");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to revoke access");
    } finally {
      setSavingRoom(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" data-testid="admin-user-access-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">
            Manage privileges — {user?.name || user?.email || "user"}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Link a Company and grant granular Data Room access. Changes apply immediately.
          </DialogDescription>
        </DialogHeader>

        {loading || !detail ? (
          <div className="flex justify-center py-12">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Linked Company */}
            <section className="space-y-2" data-testid="admin-user-access-company-section">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <Building2 className="h-4 w-4 text-primary" />
                Linked company
              </div>
              {detail.company ? (
                <div className="flex items-center justify-between rounded-xl border border-primary/30 bg-primary/5 px-3 py-2.5">
                  <div className="min-w-0">
                    <div className="text-sm font-medium truncate" data-testid="admin-user-access-linked-name">
                      {detail.company.name}
                    </div>
                    <div className="text-[11px] text-muted-foreground truncate">
                      {[detail.company.industry, detail.company.region, detail.company.stage]
                        .filter(Boolean)
                        .join(" · ") || "No metadata"}
                    </div>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={savingCompany}
                    onClick={() => linkCompany(null)}
                    className="rounded-lg"
                    data-testid="admin-user-access-unlink-company"
                  >
                    {savingCompany ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Unlink"}
                  </Button>
                </div>
              ) : (
                <p className="text-xs text-muted-foreground">Not linked to any company yet.</p>
              )}

              <div className="relative">
                <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  value={companyQuery}
                  onChange={(e) => setCompanyQuery(e.target.value)}
                  placeholder="Search companies to link…"
                  className="rounded-xl pl-9"
                  data-testid="admin-user-access-company-search"
                />
              </div>
              <div className="max-h-40 overflow-y-auto rounded-xl border border-border divide-y divide-border">
                {filteredCompanies.length === 0 ? (
                  <div className="px-3 py-4 text-xs text-muted-foreground text-center">No matches</div>
                ) : (
                  filteredCompanies.map((c) => {
                    const isLinked = detail.company?.company_id === c.company_id;
                    return (
                      <button
                        key={c.company_id}
                        type="button"
                        onClick={() => !isLinked && linkCompany(c.company_id)}
                        disabled={savingCompany || isLinked}
                        className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between transition-colors ${
                          isLinked ? "bg-primary/5 cursor-default" : "hover:bg-accent"
                        }`}
                        data-testid={`admin-user-access-company-option-${c.company_id}`}
                      >
                        <span className="truncate">{c.name}</span>
                        {isLinked && <Check className="h-3.5 w-3.5 text-primary flex-shrink-0" />}
                      </button>
                    );
                  })
                )}
              </div>
            </section>

            {/* Data rooms */}
            <section className="space-y-2" data-testid="admin-user-access-rooms-section">
              <div className="flex items-center gap-2 text-sm font-semibold">
                <FolderLock className="h-4 w-4 text-primary" />
                Data room access
                <span className="ml-1 rounded-full bg-muted px-2 py-0.5 text-[10px] uppercase tracking-widest text-muted-foreground">
                  {linkedRoomIds.size} granted
                </span>
              </div>
              <div className="relative">
                <Search className="h-4 w-4 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2" />
                <Input
                  value={roomQuery}
                  onChange={(e) => setRoomQuery(e.target.value)}
                  placeholder="Search rooms by company…"
                  className="rounded-xl pl-9"
                  data-testid="admin-user-access-room-search"
                />
              </div>
              <div className="max-h-64 overflow-y-auto rounded-xl border border-border divide-y divide-border">
                {filteredRooms.length === 0 ? (
                  <div className="px-3 py-4 text-xs text-muted-foreground text-center">No rooms</div>
                ) : (
                  filteredRooms.map((r) => {
                    const linked = linkedRoomIds.has(r.room_id);
                    const currentRole = linkedRoomRoles[r.room_id] || "viewer";
                    const isSaving = savingRoom === r.room_id;
                    return (
                      <div
                        key={r.room_id}
                        className="px-3 py-2 flex items-center justify-between gap-2"
                        data-testid={`admin-user-access-room-row-${r.room_id}`}
                      >
                        <div className="min-w-0 flex-1">
                          <div className="text-sm font-medium truncate">{r.company_name || "Untitled room"}</div>
                          <div className="text-[11px] text-muted-foreground truncate">
                            {r.description || r.stage || r.status || "—"}
                          </div>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          {linked ? (
                            <>
                              <Select
                                value={currentRole}
                                onValueChange={(v) => grantRoom(r.room_id, v)}
                                disabled={isSaving}
                              >
                                <SelectTrigger
                                  className="h-8 w-[110px] rounded-lg text-xs"
                                  data-testid={`admin-user-access-room-role-${r.room_id}`}
                                >
                                  <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                  {ROOM_ROLES.map((rr) => (
                                    <SelectItem key={rr} value={rr} className="text-xs">
                                      {rr}
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <Button
                                size="sm"
                                variant="ghost"
                                disabled={isSaving}
                                onClick={() => revokeRoom(r.room_id)}
                                className="h-8 rounded-lg text-rose-600 hover:bg-rose-50"
                                data-testid={`admin-user-access-room-revoke-${r.room_id}`}
                              >
                                {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <X className="h-3.5 w-3.5" />}
                              </Button>
                            </>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={isSaving}
                              onClick={() => grantRoom(r.room_id, "viewer")}
                              className="h-8 rounded-lg text-xs"
                              data-testid={`admin-user-access-room-grant-${r.room_id}`}
                            >
                              {isSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Grant"}
                            </Button>
                          )}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </section>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            className="rounded-xl"
            onClick={() => onOpenChange(false)}
            data-testid="admin-user-access-close"
          >
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AdminUserAccessDialog;
